import {
    BASKET_PROPOSITION_BNT,
    BASKET_PROPOSITION_HARDWAREONLY,
    BASKET_PROPOSITION_REDPLUS,
    SHOP_URL,
    createBasket,
    createBasketItem,
    createBasketProposition,
    createSailsReadyEvent,
    createUrlWithQueryString,
    decodeHtml,
    generateDeeplink,
    hasQueryParam,
    updateUrl,
} from '@vfde-sails/utils';
import {
    APP,
    CONTAINER_HARDWARE_ONLY,
    DEEPLINK_SESSION_STORAGE_KEY,
} from '../../app/constants';
import { startAppListening } from '../../app/listener';
import {
    changeCapacity,
    changeColor,
    checkTradeInEligibility,
    goToBasket,
    selectCurrentCapacity,
    selectCurrentColor,
    selectCustomerConfig,
    setAtomicId,
    setInitialState,
    setTradeIn,
    skipConnectMore,
    toggleAccordionItem,
    toggleHardwareOnly,
    updateStorage,
    setSubscriptionId,
} from './slice';
import {
    GetHardwareDetailGroupArgs,
    GetTariffWithHardwareArgs,
    getHardwareDetailGroup,
    getTariffWithHardware,
} from '../../api/glados';
import {
    bindActionCreators,
    isAnyOf,
} from '@reduxjs/toolkit';
import {
    selectAtomicDevices,
    selectAtomicIds,
    selectAvailableCapacitiesForColor,
    selectCapacityBySortValue,
    selectIsLoading,
    selectPageViewTrackingData,
    selectTariff,
} from './selectors';
import {
    createPageViewTrackingData,
    createPageViewTrackingDataWithError,
} from '../../helpers/tracking';
import {
    TRACKING_INFORMATION_ACTION_REVEAL,
    TrackType,
    TrackingError,
    TrackingErrorType,
    TrackingInformationTrigger,
    TrackingInformationUiType,
    trackIt,
} from '@vfde-sails/tracking';
import { updateStorage as updateStorageHelper } from '@vfde-sails/storage';
import {
    SAILS_BNT_STORAGE,
    SAILS_PARAM_SALESCHANNEL,
    SAILS_PARAM_SUB_ID,
    SAILS_PARAM_ATOMIC_ID,
    SAILS_PARAM_DEVICE_ID,
    SAILS_PARAM_GIGAKOMBI,
    SAILS_PARAM_INSURANCE,
    SAILS_PARAM_TRADE_IN,
    SAILS_PARAM_TAUSCHBONUS,
    SAILS_PARAM_HARDWAREONLY,
    SAILS_PARAM_FLOW_TYPE,
    URL_PARAM_ATOMIC_ID,
    SALESCHANNEL_REDPLUS,
} from '@vfde-sails/constants';
import { SailsDeviceDetailsStorage } from '../../interfaces/storage';
import { getNewSelectedCapacity } from './helpers/capacityHelper';
import { getAllDiscounts } from '@vfde-sails/page-options';
import { initTradeIn } from '../tradeIn';
import { setInitialState as setInitialTradeInState } from '../tradeIn/slice';
import { getTradeInDeviceFromStorage } from '../tradeIn/helpers/getTradeIn';

/**
 * Matcher is defined as variable so the action type is correctly infered.
 * @see https://github.com/reduxjs/redux-toolkit/discussions/4018
 */
const isHardwareDetailGroupOrTariffWithHardwareFulfilledMatcher = isAnyOf(
    getHardwareDetailGroup.matchFulfilled,
    getTariffWithHardware.matchFulfilled,
);

/**
 * See comment above
 */
const isHardwareDetailGroupOrTariffWithHardwareRejectedMatcher = isAnyOf(
    getHardwareDetailGroup.matchRejected,
    getTariffWithHardware.matchRejected,
);

export const listeners = {
    /**
     * Adds the listener for the `setInitialState.fulfilled` case.
     * It then initiates the `getHardwareDetailGroup` request.
     */
    setInitialStateFulfilled: () => startAppListening({
        actionCreator: setInitialState.fulfilled,
        effect: (action, listenerApi) => {
            const { customerConfig } = action.payload;
            const { subscriptionId } = customerConfig;

            listenerApi.dispatch(checkTradeInEligibility(customerConfig));
            listenerApi.dispatch(getHardwareDetailGroup.initiate({ subscriptionId }));
        },
    }),
    /**
     * Adds the listener for the `setInitialState.rejected` case.
     * It then redirects to the given error page URL.
     */
    setInitialStateRejected: () => startAppListening({
        actionCreator: setInitialState.rejected,
        effect: action => {
            action.payload && window.location.replace(action.payload);
        },
    }),
    /**
     * Adds the listener for the `checkTradeInEligibility.fulfilled` case.
     */
    checkTradeInEligibilityFulfilled: () => startAppListening({
        actionCreator: checkTradeInEligibility.fulfilled,
        effect: (action, listenerApi) => {
            const customerConfig = action.payload;
            const selectedTradeInDevice = getTradeInDeviceFromStorage();

            // first init tradeIn feature to start listeners
            initTradeIn();

            // then dispatch the setInitialState action of the tradeIn slice
            listenerApi.dispatch(setInitialTradeInState({ customerConfig, selectedTradeInDevice }));
        },
    }),
    /**
     * Adds the listener for the `getHardwareDetailGroup.matchFulfilled` case.
     * It then checks if the user has selected a tariff ID and initiates the `getTariffWithHardware` request.
     */
    getHardwareDetailGroupFulfilled: () => startAppListening({
        matcher: getHardwareDetailGroup.matchFulfilled,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const customerConfig = selectCustomerConfig(state);
            const { subscriptionId } = customerConfig;

            if (subscriptionId) {
                const atomicIds = selectAtomicIds(state);
                const { isTauschbonus } = customerConfig;

                const args: GetTariffWithHardwareArgs = {
                    atomicIds,
                    isTauschbonus,
                };

                listenerApi.dispatch(
                    getTariffWithHardware.initiate(args),
                );
            }
        },
    }),
    /**
     * Adds the listener for the `getHardwareDetailGroup.matchFulfilled` and `getTariffWithHardware.matchFulfilled` case.
     * It then checks if all API requests are done. If so, the public SAILS functions are registered
     * and the 'pageview' event is tracked.
     */
    hardwareOrTariffApiRequestFulfilled: () => startAppListening({
        matcher: isHardwareDetailGroupOrTariffWithHardwareFulfilledMatcher,
        effect: (action, listenerApi) => {
            const state = listenerApi.getState();
            const customerConfig = selectCustomerConfig(state);
            const { subscriptionId } = customerConfig;

            if (subscriptionId && getHardwareDetailGroup.matchFulfilled(action)) {
                // we have a subscriptionId so we first wait for the tariff response
                // to do the pageview tracking only once
                return;
            }

            const actionDispatchers = bindActionCreators({
                skipConnectMore,
                goToBasket,
                setSubscriptionId,
            }, listenerApi.dispatch);

            createSailsReadyEvent([
                {
                    actions: {
                        skipConnectMore: actionDispatchers.skipConnectMore,
                        setSubscriptionId: actionDispatchers.setSubscriptionId,
                    },
                    applicationScope: APP,
                },
                {
                    actions: {
                        goToBasket: actionDispatchers.goToBasket,
                    },
                },
            ]);

            const trackingData = selectPageViewTrackingData(state);
            const pageViewTrackingData = createPageViewTrackingData(trackingData);

            trackIt(TrackType.PageView, pageViewTrackingData!);
        },
    }),
    /**
     * Adds the listener for the `getHardwareDetailGroup.matchRejected` and `getTariffWithHardware.matchRejected` case.
     * It then tracks the error 'pageview' event.
     */
    hardwareOrTariffApiRequestRejected: () => startAppListening({
        matcher: isHardwareDetailGroupOrTariffWithHardwareRejectedMatcher,
        effect: (action, listenerApi) => {
            // unsubscribe immediately because we only want to track the initial error pageview
            listenerApi.unsubscribe();

            const state = listenerApi.getState();
            const trackingData = selectPageViewTrackingData(state);

            const trackingError: TrackingError = {
                /* eslint-disable camelcase, @typescript-eslint/naming-convention */
                error_type: TrackingErrorType.Technical,
                error_message: action.payload!.message,
                error_code: action.payload!.status?.toString(),
                error_location: action.meta.arg.endpointName,
                /* eslint-enable camelcase, @typescript-eslint/naming-convention */
            };

            trackIt(
                TrackType.PageView,
                createPageViewTrackingDataWithError(trackingData, trackingError),
            );
        },
    }),
    /**
     * Adds the listener for the `updateStorage` action.
     * It then updates the SessionStorage with the latest customerConfig.
     */
    updateStorage: () => startAppListening({
        actionCreator: updateStorage,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const customerConfig = selectCustomerConfig(state);
            const {
                deviceId,
                atomicId,
                isGigakombi,
                insurance,
                isTauschbonus,
                isTradeIn,
                isHardwareOnly,
                subscriptionId,
                salesChannel,
                flowType,
            } = customerConfig;

            updateStorageHelper<SailsDeviceDetailsStorage>(
                SAILS_BNT_STORAGE,
                {
                    [SAILS_PARAM_SALESCHANNEL]: salesChannel,
                    [SAILS_PARAM_SUB_ID]: {
                        [salesChannel]: subscriptionId || null,
                    },
                    [SAILS_PARAM_ATOMIC_ID]: atomicId || null,
                    [SAILS_PARAM_DEVICE_ID]: deviceId || null,
                    [SAILS_PARAM_GIGAKOMBI]: isGigakombi,
                    [SAILS_PARAM_INSURANCE]: insurance,
                    [SAILS_PARAM_TRADE_IN]: isTradeIn,
                    [SAILS_PARAM_TAUSCHBONUS]: isTauschbonus,
                    [SAILS_PARAM_HARDWAREONLY]: isHardwareOnly,
                    [SAILS_PARAM_FLOW_TYPE]: flowType,
                    [APP]: {
                        [CONTAINER_HARDWARE_ONLY]: {
                            [deviceId!]: {
                                [SAILS_PARAM_HARDWAREONLY]: isHardwareOnly,
                            },
                        },
                    },
                },
                {
                    shouldDeepMerge: true,
                },
            );
        },
    }),
    /**
     * Adds the listener for the `changeColor` action.
     * Based on the new color, a new capacity is selected.
     */
    changeColor: () => startAppListening({
        actionCreator: changeColor,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const currentCapacity = selectCurrentCapacity(state);
            const capacitiesForColor = selectAvailableCapacitiesForColor(state);
            const capacityByDisplayLabel = selectCapacityBySortValue(state, currentCapacity!);
            const newCapacity = getNewSelectedCapacity(capacitiesForColor!, capacityByDisplayLabel!);

            listenerApi.dispatch(changeCapacity(newCapacity.sortValue));
        },
    }),
    /**
     * Adds the listener for the `changeCapacity` action.
     * Based on the current color and capacity the new atomic is selected using the `setAtomicId` action.
     */
    changeCapacity: () => startAppListening({
        actionCreator: changeCapacity,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const currentColor = selectCurrentColor(state);
            const currentCapacity = selectCurrentCapacity(state);
            const atomics = selectAtomicDevices(state);

            const newAtomicDevice = atomics
                .filter(atomic => {
                    const { capacity, color } = atomic;

                    return color.displayLabel === currentColor && capacity.sortValue === currentCapacity;
                })[0];

            listenerApi.dispatch(setAtomicId(newAtomicDevice.atomicId));
        },
    }),
    /**
     * Adds the listener for the `setAtomicId` action.
     * It updates the URL (if needed) and tracks a pageview event.
     */
    setAtomicId: () => startAppListening({
        actionCreator: setAtomicId,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const customerConfig = selectCustomerConfig(state);
            const { atomicId } = customerConfig;
            const trackingData = selectPageViewTrackingData(state);
            const pageViewTrackingData = createPageViewTrackingData(trackingData);

            if (hasQueryParam(URL_PARAM_ATOMIC_ID)) {
                // if there is the atomicId URL param set, update if with the new atomicId
                updateUrl(
                    createUrlWithQueryString(URL_PARAM_ATOMIC_ID, `${atomicId!}`),
                    true,
                );
            }

            trackIt(
                TrackType.PageView,
                pageViewTrackingData!,
            );
        },
    }),
    /**
     * Adds the listener for the `toggleHardwareOnly` action.
     * It tracks a pageview event.
     */
    toggleHardwareOnly: () => startAppListening({
        actionCreator: toggleHardwareOnly,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const trackingData = selectPageViewTrackingData(state);
            const pageViewTrackingData = createPageViewTrackingData(trackingData);

            trackIt(
                TrackType.PageView,
                pageViewTrackingData!,
            );
        },
    }),
    /**
     * Adds the listener for the `toggleAccordionItem` action.
     * It tracks a pageview event.
     */
    toggleAccordionItem: () => startAppListening({
        actionCreator: toggleAccordionItem,
        effect: action => {
            if (!action.payload.optOpen) {
                return;
            }

            const trackingPayload = {
                /* eslint-disable camelcase, @typescript-eslint/naming-convention */
                information_action: TRACKING_INFORMATION_ACTION_REVEAL,
                information_ui_type: TrackingInformationUiType.Faq,
                information_trigger: TrackingInformationTrigger.Accordion,
                information_name: decodeHtml(action.payload.stdHeadline),
                /* eslint-enable camelcase, @typescript-eslint/naming-convention */
            };

            trackIt(
                TrackType.Information,
                trackingPayload,
            );
        },
    }),
    /**
     * Adds a listener to handle changes in the customerConfig and to update the sessionStorage.
     */
    customerConfigChanged: () => startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectCustomerConfig(currentState) !== selectCustomerConfig(previousState)
        ,
        effect: (_action, listenerApi) => {
            // update storage on every change of the customerConfig
            listenerApi.dispatch(updateStorage());
        },
    }),
    /**
     * Adds the listener for the `goToBasket` action.
     */
    goToBasket: () => startAppListening({
        actionCreator: goToBasket,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const customerConfig = selectCustomerConfig(state);
            const subscription = selectTariff(state);

            const {
                isHardwareOnly,
                salesChannel,
                insurance,
                atomicId,
                isGigakombi,
                subscriptionId,
                isTradeIn,
                isTauschbonus,
                isRestlaufzeit,
                deviceId,
            } = customerConfig;

            const discountIds = getAllDiscounts({
                salesChannel,
                deviceId,
                subscriptionIds: subscriptionId ? [subscriptionId] : [],
                isGigakombi,
                isTradeIn,
                isTauschbonus,
                isRestlaufzeit,
            });

            const serviceIds: string[] = [];

            if (!isHardwareOnly && insurance && atomicId) {
                serviceIds.push(insurance);
            }

            const basketItem = createBasketItem({
                sublevelTariffId: isHardwareOnly
                    ? undefined
                    : subscription?.subLevelId,
                ...atomicId && { atomicDeviceId: atomicId.toString() },
                ...!isHardwareOnly && {
                    serviceIds,
                    discountIds,
                },
            });

            const propositionType =
                isHardwareOnly
                    ? BASKET_PROPOSITION_HARDWAREONLY
                    : salesChannel === SALESCHANNEL_REDPLUS
                        ? BASKET_PROPOSITION_REDPLUS
                        : BASKET_PROPOSITION_BNT
                    ;

            const proposition = createBasketProposition(propositionType, [basketItem]);

            const basket = createBasket(salesChannel, [proposition]);
            const url = generateDeeplink(basket, true);

            // Write deeplink into the storage for simplicity (with double quotes, because... simplicity)
            sessionStorage.setItem(DEEPLINK_SESSION_STORAGE_KEY, `"${url.replace(SHOP_URL, '')}"`);

            window.location.assign(url);
        },
    }),
    /**
     * Adds the listener for the `setIsTradeIn` action.
     * It then checks if tauschbonus is eligible and the user has a tariff
     * and re-requests the tariff API to receive the updated prices
     * including the tauschbonus discount.
     */
    setIsTradeIn: () => startAppListening({
        actionCreator: setTradeIn,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const customerConfig = selectCustomerConfig(state);
            const { subscriptionId } = customerConfig;

            if (!subscriptionId) {
                return;
            }

            const atomicIds = selectAtomicIds(state);
            const { isTauschbonus } = customerConfig;

            const args: GetTariffWithHardwareArgs = {
                atomicIds,
                isTauschbonus,
            };

            listenerApi.dispatch(
                getTariffWithHardware.initiate(args),
            );
        },
    }),
    /**
     * Adds the listener for when the subscriptionId has changed.
     * It then initiates the `getHardwareDetailGroup` action to re-fetch
     * all hardware and tariff related data.
     * The new tariff service request will be initiated
     * from within `getHardwareDetailGroupFulfilled`.
     */
    subscriptionIdChanged: () => startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectCustomerConfig(previousState).subscriptionId !== selectCustomerConfig(currentState).subscriptionId
        ,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const isLoading = selectIsLoading(state);

            if (isLoading) {
                // ignore changes during initial requests
                // we dont need to dispatch unnecessary actions
                return;
            }

            const customerConfig = selectCustomerConfig(state);
            const { subscriptionId } = customerConfig;

            const getHardwareDetailGroupArgs: GetHardwareDetailGroupArgs = {
                subscriptionId,
            };

            listenerApi.dispatch(getHardwareDetailGroup.initiate(getHardwareDetailGroupArgs));
        },
    }),
};

/**
 * Starts all listeners.
 * Returns a function to unsubscribe all listeners.
 */
export const startListeners = () => {
    const unsubscribeListeners = Object.values(listeners).map(listener => listener());

    return () => {
        unsubscribeListeners.forEach(unsubscribeListener => unsubscribeListener());
    };
};
